import {Injectable} from "@angular/core";
import {Observable, of, switchMap, take} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {NotificationService} from "@pb-labs/notification";
import {invalidFileMessage, invalidFileSizeMessage, MAX_VALID_FILE_SIZE} from "./const";
import {UploadFileResponse} from "@app/services/sftp-api/models";
import {SFTPAPIService} from "@app/services/sftp-api/sftp-api.service";
import {TreeNode} from "@app/modules/models";
import {ForceUploadVerificationModalService} from "@app/modules/browse/components/file-upload-modal/components/force-upload-verification-modal/force-upload-verification-modal.service";
import {FileUploadModalBaseModel} from "@app/modules/browse/components/shared/file-upload-modal/services/file-upload-modal-base.model";

@Injectable()
export class FileUploadModalService extends FileUploadModalBaseModel {
  private _selectedFolder: TreeNode = null;
  private _validationErrorMessage: string = null;

  constructor(
    protected sftpAPIService: SFTPAPIService,
    protected notificationService: NotificationService,
    private forceUploadVerificationModalService: ForceUploadVerificationModalService
  ) {
    super();

    this.sftpAPIService = sftpAPIService;
  }

  destroy(): void {
    super.destroy();

    this._validationErrorMessage = null;
    this._selectedFolder = null;
  }

  get validationErrorMessage(): string {
    return this._validationErrorMessage;
  }

  startDialog$(selectedFolder: TreeNode): Observable<void> {
    this._selectedFolder = selectedFolder;

    return new Observable<void>((subscriber) => {
      this._resultSubscriber$ = subscriber;

      this._openDialog();
    });
  }

  onFileSelect(selectedFile: File): void {
    super.onFileSelect(selectedFile);

    this._validationErrorMessage = this.validateUploadedFile(selectedFile);
  }

  uploadFile(): Observable<UploadFileResponse> {
    return this._uploadFile(this._selectedFile$.value).pipe(
      switchMap((response: UploadFileResponse) => {
        let resultObservable = of(response);

        if (!response.file_uploaded) {
          resultObservable = this.verifyUploadExistingFile(this._selectedFile$.value);
        }

        return resultObservable;
      })
    );
  }

  clearFile(): void {
    super.clearFile();
    this._validationErrorMessage = null;
  }

  protected handleSuccessUpload(result: UploadFileResponse): void {
    if (result?.file_uploaded) {
      this.closeUploadModal();
      this._resultSubscriber$.next();
      this._resultSubscriber$.complete();
    }
  }

  protected closeUploadModal(): void {
    this._validationErrorMessage = null;
    this._selectedFolder = null;
    this.forceUploadVerificationModalService.closeModal();
    super.closeUploadModal();
  }

  private verifyUploadExistingFile(selectedFile: File): Observable<UploadFileResponse> {
    return new Observable((subscriber) => {
      this.forceUploadVerificationModalService
        .checkWarning(selectedFile.name)
        .pipe(take(1), takeUntil(this._destroy$))
        .subscribe({
          next: () => {
            this._uploadFile(selectedFile, true).subscribe({
              next: (response: UploadFileResponse) => {
                subscriber.next(response);
                subscriber.complete();
              },
              error: () => {
                subscriber.error();
              },
            });
          },
          error: () => {
            subscriber.next();
            subscriber.complete();
          },
        });
    });
  }

  private _uploadFile(file: File, forceOverrideFile = false): Observable<UploadFileResponse> {
    const formData = new FormData();
    formData.append("filetoupload", file);
    formData.append("file_path", this._selectedFolder.data);
    formData.append("file_name", file.name);

    return this.sftpAPIService.uploadFile(formData, forceOverrideFile);
  }

  private validateUploadedFile(file: File): string {
    let validationMessage = null;

    if (!file) {
      validationMessage = invalidFileMessage;
    }

    if (file?.size > MAX_VALID_FILE_SIZE) {
      validationMessage = invalidFileSizeMessage;
    }

    return validationMessage;
  }
}
